/* ===========================================================================
   pm-back-in-stock.js  (August 2026)
   ---------------------------------------------------------------------------
   Powers the inline sold-out "notify me when available" capture rendered by
   snippets/pm-back-in-stock.liquid on every PM PDP (apparel, tee, candle,
   blanket).

   Two jobs:
   1. Show/hide the capture by watching the buy form's [data-pm-atc] button.
      This theme flips the ATC to disabled + "Sold out" in its own variant JS,
      so we observe that state instead of duplicating variant logic. When the
      selected variant is sold out, the capture appears; when available, it
      hides. Zero coupling to each section's inline script.
   2. On submit, POST directly to Klaviyo's client-side Back in Stock endpoint
      with the LIVE selected variant id (read from [data-pm-variant-id] at
      submit time). Public company_id only. Restock-alert ONLY - we never add
      the shopper to a marketing list here.

   Klaviyo composite variant id format for Shopify: $shopify:::$default:::<id>
   Endpoint: https://a.klaviyo.com/client/back-in-stock-subscriptions/
   A 202 (no body) means success.
   =========================================================================== */
(function () {
  'use strict';

  // Posts signups to the PM location-aware BIS Apps Script queue, NOT Klaviyo's
  // native back-in-stock. Native BIS watches TOTAL inventory across locations,
  // so a Bala restock false-fires "it's back" while Bracebridge is still empty.
  // The Apps Script only notifies when Bracebridge (the online location) restocks.
  // FILL AFTER DEPLOY: paste the web-app /exec URL and the ?token secret.
  var QUEUE_ENDPOINT = 'https://script.google.com/macros/s/AKfycbwXq3_fXTYVbuwbpIQBlZzAN5Mw-StEJXrZqgSfC3W_jzxdErCVsgimXtPPiTaUMSW7IQ/exec?token=SqsMjXMLhpoPpv7r6SLEinoJ0hOzhU3Q';
  var EMAIL_RE   = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  function isSoldOut(atc) {
    if (!atc) return false;
    // A button waiting on a size choice is NOT sold out. pm-require-size.js
    // parks the ATC in a "Select a size" state on every in-stock product with
    // a real size choice; without this guard the restock signup would appear
    // on every one of them. That file avoids the disabled attribute for the
    // same reason, so this is the second line of defence, not the first.
    if (atc.hasAttribute('data-pm-awaiting-size')) return false;
    // Judge by the LABEL, never by the disabled attribute alone. The theme
    // disables the button transiently while an add-to-cart request is in
    // flight, and treating that as sold out made the capture form flash on
    // screen for a beat on every single add (reported Aug 26, 2026). The
    // section JS and the Liquid both set disabled and "Sold out" together, so
    // the text is the honest signal and the attribute is just noise.
    return /sold\s*out/i.test(atc.textContent || '');
  }

  function initOne(bis) {
    // Scope to the section this capture belongs to, so we grab the right ATC
    // and variant input even if two PDPs ever coexisted on one page.
    var root = bis.closest('[data-section-id]') || document;
    var atc = root.querySelector('[data-pm-atc]');
    var variantInput = root.querySelector('[data-pm-variant-id]');
    var email = bis.querySelector('[data-pm-bis-email]');
    var button = bis.querySelector('[data-pm-bis-submit]');
    var msg = bis.querySelector('[data-pm-bis-msg]');

    if (!email || !button) return;

    function showMsg(text, kind) {
      if (!msg) return;
      msg.textContent = text;
      msg.className = 'pm-bis__msg is-' + kind;
      msg.hidden = false;
    }
    function clearMsg() {
      if (!msg) return;
      msg.hidden = true;
      msg.textContent = '';
    }

    // --- 1. Visibility: mirror the ATC sold-out state ----------------------
    function sync() {
      var soldOut = isSoldOut(atc);
      bis.hidden = !soldOut;
      if (!soldOut) clearMsg(); // reset when switching back to an available variant
    }
    sync();
    if (atc && 'MutationObserver' in window) {
      new MutationObserver(sync).observe(atc, {
        attributes: true,
        // data-pm-awaiting-size matters as much as disabled here. When the
        // shopper picks a sold-out size, the section JS sets disabled + "Sold
        // out" first and pm-require-size.js clears the awaiting flag a tick
        // later. Without watching that attribute we'd evaluate on the theme's
        // change while the flag was still set, decide "not sold out", and
        // never re-check. The capture form would never appear.
        attributeFilter: ['disabled', 'data-pm-awaiting-size'],
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    // --- 2. Submit ---------------------------------------------------------
    function submit() {
      var addr = (email.value || '').trim();
      if (!EMAIL_RE.test(addr)) {
        showMsg("That email doesn't look right.", 'err');
        email.focus();
        return;
      }
      var variantId = variantInput && variantInput.value;
      if (!variantId) {
        showMsg('Something went wrong. Try again in a moment.', 'err');
        return;
      }

      button.disabled = true;
      showMsg('Adding you to the list...', 'info');

      var productTitle = '';
      var ogt = document.querySelector('meta[property="og:title"]');
      if (ogt) productTitle = ogt.getAttribute('content') || '';

      // Post to the PM location-aware BIS queue (Apps Script). no-cors because
      // Apps Script web apps don't return CORS headers; the request still lands,
      // but the response is opaque, so we treat "sent" as success (server logs
      // catch any real failures). text/plain skips the CORS preflight.
      fetch(QUEUE_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ type: 'signup', email: addr, variant_id: variantId, product_title: productTitle })
      })
        .then(function () {
          showMsg("You're on the list. We'll email you the moment it's back.", 'ok');
          email.value = '';
          button.disabled = false;
        })
        .catch(function (err) {
          if (window.console) console.warn('[PM BIS]', err);
          showMsg('Something went wrong. Try again in a moment.', 'err');
          button.disabled = false;
        });
    }

    button.addEventListener('click', submit);
    email.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
    });
    email.addEventListener('input', function () {
      if (msg && !msg.hidden && msg.classList.contains('is-err')) clearMsg();
    });

    bis.setAttribute('data-pm-bis-ready', '1');
  }

  function init() {
    document.querySelectorAll('[data-pm-bis]:not([data-pm-bis-ready])').forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
